import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/redux/store/store";
import { CompanyGrossPayView, CompanyGrossPayInput, AmountCalculationType, GrossPayType, Taxability } from "../../../../models/payroll";
import { GetCompanyGrossPays, AddCompanyGrossPay, EditCompanyGrossPay, DeleteCompanyGrossPay } from "../../../../api/Payroll/grossPay";
import Pagination from "../../../../components/Pagination";
import CompanyGrossPayDialog from "./CompanyGrossPayDialog";
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from "../../../../components/ui/Dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import { Spinner } from "../../../../components/ui/Spinner";
import AppIcon from "../../../../components/AppIcon";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { StatusBadge } from "../../../../components/ui/StatusBadge";
import { PageLayout } from "../../../../components/ui/PageLayout";
import { Check, X, DollarSign } from "lucide-react";

const GrossPayConfig = () => {
  const { t } = useTranslation(['payroll', 'common']);
  const company = useSelector((state: RootState) => state.user.company);
  const { toast, showToast, dismissToast } = useToast();

  const [salaryComponents, setSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [fixedSalaryComponents, setFixedSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [filteredSalaryComponents, setFilteredSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(10);
  const [offset, setOffset] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<"add" | "edit">("add");
  const [currentInput, setCurrentInput] = useState<CompanyGrossPayInput>({
    id: 0,
    name: "",
    amountCalculationType: AmountCalculationType.EmployeeSpecificAmount,
    type: GrossPayType.Allowance,
    taxability: Taxability.Exempted,
    proofRequired: false,
    amount: 0.0,
    formulaPercentage: 0.0,
    companyGrossPayForFormulaId: 0,
    isPrimary: false,
    includedInWps: true,
  });
  const [editId, setEditId] = useState(0);
  const [deleteId, setDeleteId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ type: "", taxability: "" });
  const [amountCalculation, setAmountCalculation] = useState("");
  const [hideFormula, setHideFormula] = useState(false);
  const [hideAmount, setHideAmount] = useState(false);

  const amountCalculationOptions = ['Employee Specific Amount', 'Formula Based Amount', 'Fixed Amount'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const components = await GetCompanyGrossPays();
      const fixedComponents = components.payload.filter((c: CompanyGrossPayView) => c.amountCalculationType === AmountCalculationType.FixedAmount || c.amountCalculationType === AmountCalculationType.EmployeeSpecificAmount);
      setSalaryComponents(components.payload);
      setFilteredSalaryComponents(components.payload);
      setFixedSalaryComponents(fixedComponents);
      setLoading(false);
    } catch (ex) {
      console.log(ex);
      setLoading(false);
    }
  };

  const filterData = (newState: any) => {
    setFilter((prevState) => ({ ...prevState, ...newState }));
    setFilteredSalaryComponents(
      salaryComponents.filter(
        (u) =>
          (!newState.type || u.type?.toLowerCase().includes(newState.type.toLowerCase())) &&
          (!newState.taxability || u.taxability?.toLowerCase().includes(newState.taxability.toLowerCase()))
      )
    );
  };

  const handleOpenDialog = (type: "add" | "edit", input?: CompanyGrossPayInput, id?: number) => {
    setDialogType(type);
    if (type === "edit" && input && id !== undefined) {
      setCurrentInput(input);
      setEditId(id);
    } else {
      setCurrentInput({
        id: 0,
        name: "",
        amountCalculationType: AmountCalculationType.EmployeeSpecificAmount,
        type: GrossPayType.Allowance,
        taxability: Taxability.Exempted,
        proofRequired: false,
        amount: undefined,
        formulaPercentage: undefined,
        companyGrossPayForFormulaId: undefined,
        isPrimary: false,
        includedInWps: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async (input: CompanyGrossPayInput) => {
    setLoading(true);
    try {
      if (dialogType === "add") {
        await AddCompanyGrossPay(input);
      } else {
        await EditCompanyGrossPay(input);
      }
      handleCloseDialog();
      fetchData();
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description)
            showToast(err.description, 'error');
        });
      }
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await DeleteCompanyGrossPay(deleteId);
      setDeleteId(0);
      fetchData();
      setLoading(false);
    } catch (ex: any) {
      console.log(ex);
      if (ex && ex.status === 406) {
        showToast(t("payroll:Cannot delete salary component because it is assigned to users") ?? "", 'error');
      }
      setLoading(false);
    }
  };

  const onPageChange = (page: number): void => {
    setCurrentPage(page);
    setOffset((page - 1) * count);
  };

  return (
    <PageLayout
      title={t('payroll:Gross Pays')}
      description={t('payroll:Manage compensation & allowance components')}
      icon={DollarSign}
      accentColor="emerald"
      headerActions={(
        <Button
          variant="outline"
          iconName="PlusCircle"
          onClick={() => handleOpenDialog('add')}
          data-testid="add-type-btn"
        >
          {t('payroll:Add Gross Pay')}
        </Button>
      )}
    >
      <div className="flex flex-col gap-5 flex-1">
      {/* Table */}
      <Table aria-label="gross-pays-table" data-testid="settings-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t('payroll:Component Name')}</TableHead>
            <TableHead>{t('payroll:Component Type')}</TableHead>
            <TableHead>{t('payroll:Taxability')}</TableHead>
            <TableHead>{t('payroll:Proof Required')}</TableHead>
            <TableHead>{t('common:includedInWps')}</TableHead>
            <TableHead>{t('payroll:Amount')}</TableHead>
            <TableHead>{t('payroll:Formula')}</TableHead>
            <TableHead>{t('common:Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!loading ? (
            filteredSalaryComponents
              .slice(offset, Math.min(offset + count, filteredSalaryComponents.length))
              .map((row) => {
                const formulaDisplay = row.amountCalculationType === AmountCalculationType.FormulaBasedAmount
                  ? `${row.formulaPercentage}% ${row.companyGrossPayForFormula?.name}`
                  : '';
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-foreground">{row.name}</span>
                        {row.isPrimary && (
                          <StatusBadge variant="info" size="sm">
                            {t('payroll:Primary Component')}
                          </StatusBadge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{t(`payroll:${row.type}`)}</TableCell>
                    <TableCell className="text-sm">{t(`payroll:${row.taxability}`)}</TableCell>
                    <TableCell className="text-sm">
                      <StatusBadge variant={row.proofRequired ? 'warning' : 'neutral'} size="sm">
                        {row.proofRequired ? t('common:Yes') : t('common:No')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.includedInWps ? (
                        <Check size={16} className="text-green-600" />
                      ) : (
                        <X size={16} className="text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {row.amount && row.amount !== 0 ? row.amount : ''}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formulaDisplay}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={String(t('common:Edit'))}
                          onClick={() => handleOpenDialog('edit', row, row.id)}
                        >
                          <AppIcon name="Pencil" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={String(t('common:Delete'))}
                          onClick={() => setDeleteId(row.id)}
                        >
                          <AppIcon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-6">
                <Spinner size="md" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <CompanyGrossPayDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSubmit}
        input={currentInput}
        fixedSalaryComponents={fixedSalaryComponents}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteId !== 0} onOpenChange={(open) => { if (!open) setDeleteId(0); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AppIcon name="Trash2" size={18} className="text-primary" />
              <DialogTitle>{t('payroll:Delete Gross Pay')}</DialogTitle>
            </div>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              {t('payroll:Are you sure you want to delete this component?')}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteId(0)}
              disabled={loading}
            >
              {t('common:Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={loading}
            >
              {t('common:Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(salaryComponents.length / count)}
        onPageChange={onPageChange}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default GrossPayConfig;
